'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth/AuthContext';
import { commandCenterApi } from '@/lib/api-client';
import { getAllToolScores } from '@/lib/tool-score-storage';

const STORAGE_KEY = 'due-diligence-session';

const STEPS = [
  {
    id: 'codebase-health-analyzer',
    label: 'Codebase Health',
    description: 'Evaluate code quality, testing, documentation, architecture, dependencies, performance, deployment, and monitoring.',
    href: '/tools/codebase-health-analyzer',
    icon: '🔍',
  },
  {
    id: 'architecture-maturity-assessment',
    label: 'Architecture Maturity',
    description: 'Assess maturity across 10 TOGAF-aligned dimensions including scalability, reliability, and governance.',
    href: '/tools/architecture-maturity-assessment',
    icon: '🏗️',
  },
  {
    id: 'security-assessment-checklist',
    label: 'Security Assessment',
    description: 'Review 37 security controls across authentication, data protection, application security, and compliance.',
    href: '/tools/security-assessment-checklist',
    icon: '🔒',
  },
  {
    id: 'vendor-risk-assessment',
    label: 'Vendor Risk',
    description: 'Evaluate vendor security controls, data handling, incident management, and business continuity.',
    href: '/tools/vendor-risk-assessment',
    icon: '🤝',
  },
  {
    id: 'tech-debt-prioritizer',
    label: 'Tech Debt Analysis',
    description: 'Catalog and prioritize technical debt items by impact, effort, risk, and urgency.',
    href: '/tools/tech-debt-prioritizer',
    icon: '📋',
  },
];

const CONTEXTS = [
  { value: 'acquisition', label: 'M&A / Acquisition', description: 'Evaluating technology assets for potential acquisition' },
  { value: 'investment', label: 'Investment Due Diligence', description: 'Technical assessment for investment decision' },
  { value: 'audit', label: 'Internal Audit', description: 'Comprehensive technology audit of existing systems' },
  { value: 'vendor_evaluation', label: 'Vendor Evaluation', description: 'Evaluating a vendor\'s technology capabilities' },
  { value: 'internal_review', label: 'Internal Review', description: 'Periodic review of internal technology landscape' },
];

interface StepScore {
  score: number | null;
  gaps?: Array<{ title: string; severity: string }>;
  source: 'api' | 'manual' | 'local';
}

interface SessionState {
  sessionName: string;
  targetCompany: string;
  context: string;
  started: boolean;
  completedSteps: string[];
  scores: Record<string, StepScore>;
  apiSessionId?: string;
}

function getRagColor(score: number | null): { bg: string; text: string; border: string; label: string } {
  if (score === null) return { bg: 'bg-cream/5', text: 'text-foreground-muted', border: 'border-border-muted', label: 'Not assessed' };
  if (score >= 75) return { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30', label: 'Low Risk' };
  if (score >= 50) return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Medium Risk' };
  return { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', label: 'High Risk' };
}

function getRagDot(score: number | null): string {
  if (score === null) return 'bg-cream/20';
  if (score >= 75) return 'bg-green-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function getOverallVerdict(score: number | null): { label: string; description: string; color: string } {
  if (score === null) return { label: 'Incomplete', description: 'Complete all assessments to see the overall verdict.', color: 'text-foreground-muted' };
  if (score >= 80) return { label: 'PROCEED', description: 'Technology posture is strong. Minor improvements recommended.', color: 'text-green-400' };
  if (score >= 65) return { label: 'PROCEED WITH CONDITIONS', description: 'Acceptable posture with notable gaps. Remediation plan recommended before close.', color: 'text-amber-400' };
  if (score >= 50) return { label: 'CAUTION', description: 'Significant risk areas identified. Material investment in remediation likely required.', color: 'text-accent' };
  return { label: 'DO NOT PROCEED', description: 'Critical deficiencies across multiple areas. High technical risk to business continuity.', color: 'text-red-400' };
}

export default function TechnologyDueDiligencePage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [sessionName, setSessionName] = useState('');
  const [targetCompany, setTargetCompany] = useState('');
  const [context, setContext] = useState('');
  const [started, setStarted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [scores, setScores] = useState<Record<string, StepScore>>({});
  const [apiSessionId, setApiSessionId] = useState<string | undefined>();
  const [loaded, setLoaded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fetchingScores, setFetchingScores] = useState(false);

  // Restore session from localStorage on mount, always merging local tool scores
  useEffect(() => {
    const localScores = getAllToolScores(STEPS.map(s => s.id));

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state: SessionState = JSON.parse(saved);
        setSessionName(state.sessionName || '');
        setTargetCompany(state.targetCompany || '');
        setContext(state.context || '');
        setStarted(state.started || false);
        setApiSessionId(state.apiSessionId);

        const restoredScores = { ...(state.scores || {}) };
        const restoredCompleted = [...(state.completedSteps || [])];

        // Merge local tool scores — always, not just when started
        for (const step of STEPS) {
          const local = localScores[step.id];
          if (local && (!restoredScores[step.id] || restoredScores[step.id].source !== 'api')) {
            restoredScores[step.id] = { score: local.score, source: 'local' };
          }
          if (local && !restoredCompleted.includes(step.id)) {
            restoredCompleted.push(step.id);
          }
        }

        setScores(restoredScores);
        setCompletedSteps(restoredCompleted);
      } else {
        // No session yet — still load any local tool scores
        const initialScores: Record<string, StepScore> = {};
        const initialCompleted: string[] = [];

        for (const step of STEPS) {
          const local = localScores[step.id];
          if (local) {
            initialScores[step.id] = { score: local.score, source: 'local' };
            initialCompleted.push(step.id);
          }
        }

        if (Object.keys(initialScores).length > 0) {
          setScores(initialScores);
          setCompletedSteps(initialCompleted);
        }
      }
    } catch {
      // Ignore parse errors
    }
    setLoaded(true);
  }, []);

  // Save session to localStorage on state changes (only when session started)
  const saveToLocalStorage = useCallback(() => {
    if (!loaded || !started) return;
    const state: SessionState = { sessionName, targetCompany, context, started, completedSteps, scores, apiSessionId };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors
    }
  }, [sessionName, targetCompany, context, started, completedSteps, scores, apiSessionId, loaded]);

  useEffect(() => {
    saveToLocalStorage();
  }, [saveToLocalStorage]);

  // Fetch scores from API when authenticated
  const fetchApiScores = useCallback(async () => {
    if (!isAuthenticated || !started) return;
    setFetchingScores(true);
    try {
      const response = await commandCenterApi.assessments.list('default');
      if (response.data?.assessments) {
        const assessments = response.data.assessments as Array<{
          tool_id: string;
          score_overall: number | null;
          gaps: Array<{ title: string; severity: string }> | null;
          status: string;
        }>;

        const newScores: Record<string, StepScore> = { ...scores };
        for (const step of STEPS) {
          // Find the most recent completed assessment for this tool
          const assessment = assessments
            .filter(a => a.tool_id === step.id && a.status === 'completed')
            .sort((a: any, b: any) => new Date(b.completed_at || b.created_at).getTime() - new Date(a.completed_at || a.created_at).getTime())[0];

          if (assessment && assessment.score_overall !== null) {
            newScores[step.id] = {
              score: Math.round(assessment.score_overall),
              gaps: assessment.gaps?.filter(g => g.severity === 'critical' || g.severity === 'high').slice(0, 3) || [],
              source: 'api',
            };
          }
        }
        setScores(newScores);
      }
    } catch {
      // API fetch failed — use cached/manual scores
    } finally {
      setFetchingScores(false);
    }
  }, [isAuthenticated, started, scores]);

  // Fetch API scores on mount when authenticated and session started
  useEffect(() => {
    if (loaded && isAuthenticated && started && !fetchingScores) {
      fetchApiScores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, isAuthenticated, started]);

  // Re-read local scores when page becomes visible (user returns from completing a tool)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const localScores = getAllToolScores(STEPS.map(s => s.id));

      setScores(prev => {
        const next = { ...prev };
        let changed = false;
        for (const step of STEPS) {
          const local = localScores[step.id];
          if (local && (!next[step.id] || next[step.id].source !== 'api')) {
            if (!next[step.id] || next[step.id].score !== local.score || next[step.id].source !== 'local') {
              next[step.id] = { score: local.score, source: 'local' };
              changed = true;
            }
          }
        }
        return changed ? next : prev;
      });

      setCompletedSteps(prev => {
        const next = [...prev];
        let changed = false;
        for (const step of STEPS) {
          const local = localScores[step.id];
          if (local && !next.includes(step.id)) {
            next.push(step.id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const handleStart = async () => {
    if (!sessionName || !context) return;
    setStarted(true);

    if (isAuthenticated) {
      setSyncing(true);
      try {
        const response = await commandCenterApi.dueDiligence.create({
          workspace_id: 'default',
          name: sessionName,
          target_company: targetCompany || undefined,
          context,
        });
        if (response.data?.session?.id) {
          setApiSessionId(response.data.session.id);
        }
      } catch {
        // API sync failed — localStorage still works
      } finally {
        setSyncing(false);
      }
    }
  };

  const handleReset = () => {
    setSessionName('');
    setTargetCompany('');
    setContext('');
    setStarted(false);
    setCompletedSteps([]);
    setScores({});
    setApiSessionId(undefined);
    localStorage.removeItem(STORAGE_KEY);
  };

  const toggleStep = (stepId: string) => {
    setCompletedSteps(prev =>
      prev.includes(stepId) ? prev.filter(s => s !== stepId) : [...prev, stepId]
    );
  };

  const progress = Math.round((completedSteps.length / STEPS.length) * 100);
  const scoredSteps = STEPS.filter(s => scores[s.id]?.score !== null && scores[s.id]?.score !== undefined);
  const overallScore = scoredSteps.length > 0
    ? Math.round(scoredSteps.reduce((sum, s) => sum + (scores[s.id]?.score || 0), 0) / scoredSteps.length)
    : null;
  const allScored = scoredSteps.length === STEPS.length;
  const hasAnyScore = scoredSteps.length > 0;
  const verdict = getOverallVerdict(allScored ? overallScore : null);

  if (!loaded || authLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 w-full">
        <div className="animate-pulse">
          <div className="h-8 bg-cream/10 rounded w-1/3 mb-4" />
          <div className="h-4 bg-cream/10 rounded w-2/3" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 w-full">
      <div className="mb-8">
        <Link href="/tools" className="text-foreground-tertiary hover:text-accent text-sm transition-colors">
          &larr; All Tools
        </Link>
      </div>

      <h1 className="text-3xl font-bold text-foreground mb-2">Technology Due Diligence</h1>
      <p className="text-foreground-secondary mb-8">
        Comprehensive 5-part technology assessment combining codebase health, architecture maturity,
        security posture, vendor risk, and tech debt into a unified due diligence report.
      </p>

      <div className="space-y-6">
        {/* Session header — only when a session is active */}
        {started && (
          <div className="bg-surface border border-border-muted rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{sessionName}</h2>
                {targetCompany && <p className="text-foreground-tertiary text-sm">{targetCompany}</p>}
                {syncing && <p className="text-accent text-xs mt-1">Saving to Command Center...</p>}
                {isAuthenticated && apiSessionId && !syncing && (
                  <p className="text-green-400/60 text-xs mt-1">Synced to Command Center</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-accent">{progress}%</div>
                <div className="text-foreground-tertiary text-xs">{completedSteps.length} of {STEPS.length} complete</div>
              </div>
            </div>
            <div className="w-full bg-background rounded-full h-2 mt-3">
              <div
                className="bg-accent h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Assessment tools — always visible */}
        <div className="space-y-3">
          {STEPS.map((step, idx) => {
            const isComplete = completedSteps.includes(step.id);
            const stepScore = scores[step.id];
            const hasScore = stepScore?.score !== null && stepScore?.score !== undefined;
            const rag = getRagColor(stepScore?.score ?? null);

            return (
              <div
                key={step.id}
                className={`border rounded-lg p-4 transition-colors ${
                  hasScore
                    ? `${rag.border} ${rag.bg}`
                    : 'border-border-muted bg-surface'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    hasScore
                      ? isComplete ? 'bg-green-500 text-white' : `${rag.bg} ${rag.text} border ${rag.border}`
                      : 'bg-cream/10 text-foreground-tertiary'
                  }`}>
                    {isComplete ? '✓' : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{step.icon}</span>
                      <h3 className="text-foreground font-medium">{step.label}</h3>
                      {hasScore && (
                        <span className={`w-2.5 h-2.5 rounded-full ${getRagDot(stepScore.score)}`} title={`Score: ${stepScore.score}%`} />
                      )}
                    </div>
                    <p className="text-foreground-tertiary text-sm mt-0.5">{step.description}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasScore && (
                      <div className="text-right mr-1">
                        <span className={`text-sm font-bold ${rag.text}`}>{stepScore.score}%</span>
                        <div className={`text-[10px] ${rag.text} opacity-70`}>{rag.label}</div>
                      </div>
                    )}
                    <Link href={step.href} className="text-accent text-sm hover:underline whitespace-nowrap">
                      {hasScore ? 'Review' : 'Start'} &rarr;
                    </Link>
                    {started && (
                      <button
                        onClick={() => toggleStep(step.id)}
                        className={`text-xs px-3 py-1 rounded border transition-colors whitespace-nowrap ${
                          isComplete
                            ? 'border-green-500/30 text-green-400 hover:border-green-500/50'
                            : 'border-border text-foreground-tertiary hover:border-border/40'
                        }`}
                      >
                        {isComplete ? 'Completed' : 'Mark Done'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scorecard — shows when at least one score exists */}
        {hasAnyScore && (
          <div className="bg-surface border border-border-muted rounded-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground">Due Diligence Scorecard</h2>
              {isAuthenticated && started && (
                <button
                  onClick={fetchApiScores}
                  disabled={fetchingScores}
                  className="text-xs text-foreground-muted hover:text-foreground-secondary transition-colors disabled:opacity-40"
                >
                  {fetchingScores ? 'Refreshing...' : 'Refresh scores'}
                </button>
              )}
            </div>

            {/* Overall score */}
            <div className="flex items-center gap-6 mb-6 pb-6 border-b border-border-muted">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center border-4 ${
                overallScore === null ? 'border-border-muted' :
                overallScore >= 75 ? 'border-green-500' :
                overallScore >= 50 ? 'border-amber-500' : 'border-red-500'
              }`}>
                <span className={`text-2xl font-bold ${
                  overallScore === null ? 'text-foreground-muted' :
                  overallScore >= 75 ? 'text-green-400' :
                  overallScore >= 50 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {overallScore !== null ? overallScore : '—'}
                </span>
              </div>
              <div>
                <div className={`text-lg font-bold ${verdict.color}`}>{verdict.label}</div>
                <p className="text-foreground-tertiary text-sm mt-0.5 max-w-md">{verdict.description}</p>
                {scoredSteps.length < STEPS.length && (
                  <p className="text-foreground-muted text-xs mt-1">
                    Based on {scoredSteps.length} of {STEPS.length} assessments
                  </p>
                )}
              </div>
            </div>

            {/* Per-tool RAG bars */}
            <div className="space-y-3">
              {STEPS.map(step => {
                const stepScore = scores[step.id];
                const score = stepScore?.score ?? null;
                const rag = getRagColor(score);

                return (
                  <div key={step.id} className={`rounded-lg border p-3 ${rag.border} ${rag.bg}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${getRagDot(score)}`} />
                        <span className="text-foreground text-sm font-medium">{step.label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs ${rag.text}`}>{rag.label}</span>
                        <span className={`text-sm font-bold ${rag.text}`}>
                          {score !== null ? `${score}%` : '—'}
                        </span>
                      </div>
                    </div>
                    {score !== null && (
                      <div className="w-full bg-background/50 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            score >= 75 ? 'bg-green-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                    )}
                    {stepScore?.gaps && stepScore.gaps.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {stepScore.gaps.map((gap, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                              gap.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {gap.severity}
                            </span>
                            <span className="text-foreground-tertiary truncate">{gap.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Risk summary */}
            {allScored && overallScore !== null && (
              <div className="mt-5 pt-5 border-t border-border-muted">
                <h3 className="text-sm font-semibold text-foreground mb-3">Risk Summary</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <div className="text-2xl font-bold text-red-400">
                      {STEPS.filter(s => (scores[s.id]?.score ?? 100) < 50).length}
                    </div>
                    <div className="text-xs text-red-400/70 mt-0.5">High Risk</div>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <div className="text-2xl font-bold text-amber-400">
                      {STEPS.filter(s => {
                        const sc = scores[s.id]?.score;
                        return sc !== null && sc !== undefined && sc >= 50 && sc < 75;
                      }).length}
                    </div>
                    <div className="text-xs text-amber-400/70 mt-0.5">Medium Risk</div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-400">
                      {STEPS.filter(s => (scores[s.id]?.score ?? 0) >= 75).length}
                    </div>
                    <div className="text-xs text-green-400/70 mt-0.5">Low Risk</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Session form — when no session started yet */}
        {!started && (
          <div className="bg-surface border border-border-muted rounded-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              {hasAnyScore ? 'Name Your Assessment' : 'Start New Assessment'}
            </h2>
            {hasAnyScore && (
              <p className="text-foreground-tertiary text-sm mb-4">
                Create a named session to track progress, sync to the Command Center, and export a report.
              </p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-foreground-secondary mb-1">Assessment Name *</label>
                <input
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="e.g., Acme Corp Technology Review Q1 2025"
                  className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                  maxLength={255}
                />
              </div>
              <div>
                <label className="block text-sm text-foreground-secondary mb-1">Target Company / System</label>
                <input
                  type="text"
                  value={targetCompany}
                  onChange={(e) => setTargetCompany(e.target.value)}
                  placeholder="e.g., Acme Corp"
                  className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                  maxLength={255}
                />
              </div>
              <div>
                <label className="block text-sm text-foreground-secondary mb-2">Assessment Context *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CONTEXTS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setContext(c.value)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        context === c.value
                          ? 'border-accent bg-accent-muted text-foreground'
                          : 'border-border-muted text-foreground-secondary hover:border-border/30'
                      }`}
                    >
                      <div className="font-medium text-sm">{c.label}</div>
                      <div className="text-xs mt-0.5 opacity-70">{c.description}</div>
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleStart}
                disabled={!sessionName || !context}
                className="bg-accent text-accent-foreground font-semibold px-6 py-2 rounded hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {hasAnyScore ? 'Create Session' : 'Start Due Diligence'}
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          {started && (
            <button
              onClick={handleReset}
              className="text-sm text-foreground-muted hover:text-foreground-secondary transition-colors"
            >
              Reset &amp; Start New Assessment
            </button>
          )}
          {!isAuthenticated && (
            <p className="text-xs text-foreground-muted ml-auto">
              Scores auto-populate from completed assessments. <Link href="/api/auth/login" className="text-accent hover:text-accent">Sign in</Link> to sync across devices.
            </p>
          )}
        </div>

        {/* Completion CTA */}
        {allScored && (
          <div className={`border rounded-lg p-6 text-center ${
            overallScore !== null && overallScore >= 65
              ? 'bg-green-500/10 border-green-500/30'
              : overallScore !== null
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-green-500/10 border-green-500/30'
          }`}>
            <h3 className="text-lg font-semibold text-foreground mb-1">Due Diligence Complete</h3>
            <p className="text-foreground-secondary text-sm mb-4">
              {isAuthenticated
                ? 'Your results are saved in the Command Center and can be exported as a comprehensive report.'
                : 'Sign in to save your results and generate a comprehensive report.'}
            </p>
            {isAuthenticated ? (
              <Link
                href="/command-center"
                className="inline-block bg-accent text-accent-foreground font-semibold px-6 py-2 rounded hover:bg-accent-hover transition-colors"
              >
                View in Command Center
              </Link>
            ) : (
              <Link
                href="/api/auth/login"
                className="inline-block bg-accent text-accent-foreground font-semibold px-6 py-2 rounded hover:bg-accent-hover transition-colors"
              >
                Sign In to Save Results
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
